import { Fragment, forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye } from 'lucide-react';
import { useSlide, useStudyDrag, type DropTarget } from '@/components/drag/studyDrag';
import { PreviewThumb } from '@/components/PreviewThumb';
import { cn } from '@/lib/utils';
import type { NavigatorImage } from './api';
import { buildSections, isTail, nameOf, reorder, seenTally, type NavigatorGroup, type Neighbours, type NavigatorSection, type SeenTally } from './sections';
import { DRAG_WIDTH, ImageCard, OrdinalCell } from './ImageCard';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ClampedText } from '@/components/ClampedText';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { OtherTypeIcon, TYPE_ICONS } from './typeIcons';
import { TYPE_ACRONYMS } from '@/data/imageTypes';

/** The height of a Type bar; every sticky offset is a multiple of it. */
export const BAR_H = 28;

interface Props {
  images: NavigatorImage[];
  /** Groups are told apart by specimen too (enterprise). */
  specimen: boolean;
  selectedUID: string | undefined;
  onSelect: (uid: string) => void;
  /** In compare mode, marks which pane (A–D) an image is shown in. */
  assignment?: (uid: string) => string | undefined;
  thumbnail: (image: NavigatorImage) => string | null;
  /** Given only to somebody who may reorder: the image goes next to a neighbour of its own group. */
  onReorder?: (imageId: string, target: Neighbours) => void;
  /**
   * Given only where the reader's seen marks are kept (signed in, not a share
   * link): the cards show an eye once seen, each Type's bar "Seen N / M", and
   * the foot of the panel the study's count, a bar and this reset.
   */
  onResetSeen?: () => void;
}

/**
 * The study's images by Type, then by group, in ONE scroll container, under a
 * bar of the Types present (TypeBar) that goes to any of them.
 *
 * The section on screen keeps its name pinned at the top (decided 2026-09-24:
 * one pinned, no stacking): every section's bar sticks at `top: 0`, and the
 * next one slides over it as its section arrives. That takes the bars, group
 * headers and cards being direct children of the scroller — a wrapper around
 * a section would be the bar's containing block — and no vertical padding on
 * the scroller.
 *
 * Where seen marks are kept, the foot of the panel counts them for the whole
 * study (SeenFooter), outside the scroller so it is always on screen, and can
 * collapse the images already seen to their band, without the preview (user,
 * 2026-09-27: collapse rather than hide; each keeps its place, its number and
 * its drag). For this viewing only (off each time a study opens; StudyPage
 * keys the navigator by study), and left as set however many are seen. The
 * image on screen, and each compare pane's, stays whole.
 */
export function ImageNavigator({ images, specimen, selectedUID, onSelect, assignment, thumbnail, onReorder, onResetSeen }: Props) {
  // The scroller: as a ref for the layout measurements and the FLIP, and as
  // state for the drag hook, which is handed the element rather than a ref.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const setRoot = useCallback((el: HTMLDivElement | null) => {
    rootRef.current = el;
    setScroller(el);
  }, []);

  const sections = useMemo(() => buildSections(images, specimen), [images, specimen]);
  const tracked = !!onResetSeen;
  const studySeen = useMemo(() => seenTally(images), [images]);
  // For this viewing only: off whenever a study opens (a reload included),
  // never saved, and left as the reader set it however many are seen (user,
  // 2026-09-26/27).
  const [collapseSeen, setCollapseSeen] = useState(false);
  // A seen card collapses to its band, unless its image is on screen (the one
  // in view, a compare pane's): that one stays whole.
  const collapsed = (img: NavigatorImage) =>
    tracked && collapseSeen && !!img.seen_utc && !isTail(img) && img.series_instance_uid !== selectedUID && !assignment?.(img.series_instance_uid);
  const groupOf = useMemo(() => new Map(sections.flatMap((sec) => sec.groups.flatMap((g) => g.images.map((img) => [img.image_id, g] as const)))), [sections]);

  // ── drag and drop, within the image's own group ──
  // Where a drop would put the image: after or before a neighbour, or null
  // when it would land where it already is.
  const neighboursOf = useCallback(
    (imageId: string, t: DropTarget): Neighbours | null => {
      const g = groupOf.get(imageId);
      if (!g || g.key !== t.band) return null;
      const at = g.images.findIndex((i) => i.image_id === imageId);
      if (t.beforeId) return g.images[at + 1]?.image_id === t.beforeId ? null : { beforeId: t.beforeId };
      const last = g.images[g.images.length - 1];
      return last.image_id === imageId ? null : { afterId: last.image_id };
    },
    [groupOf],
  );
  const { drag, onPointerDown, overlayRef } = useStudyDrag({
    enabled: !!onReorder,
    carry: (id) => [id],
    onDrop: (ids, t) => {
      const n = neighboursOf(ids[0], t);
      if (n) onReorder?.(ids[0], n);
    },
    scroller,
    lockBand: (id) => groupOf.get(id)?.key ?? '',
  });
  // While a drag is on, the list is laid out as if the image had already been
  // dropped where the pointer is: that is the preview.
  const shown: NavigatorSection[] = useMemo(() => {
    const n = drag?.target ? neighboursOf(drag.lead, drag.target) : null;
    return n ? buildSections(reorder(images, drag!.lead, n), specimen) : sections;
  }, [drag, neighboursOf, images, specimen, sections]);
  const orderKey = shown.map((sec) => sec.groups.map((g) => g.key + ':' + g.images.map((i) => i.image_id).join(',')).join(';')).join('|');
  useSlide(rootRef, orderKey, 'list');
  const carried = drag ? images.find((i) => i.image_id === drag.lead) : undefined;

  // ── the bars ──
  const n = shown.length;
  // A zero-height mark before each bar: the bar's own offset moves with it
  // once it sticks, the mark's is where the section begins.
  const marks = useRef<(HTMLElement | null)[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  const sectionTop = (i: number) => marks.current[i]?.offsetTop ?? 0;
  // Room after the last section, so that going to any section can land it
  // at the top: the scroller's height less the last section (its bar included).
  const [spacer, setSpacer] = useState(0);
  const [current, setCurrent] = useState(0);
  const measure = useCallback(() => {
    const el = rootRef.current;
    const end = endRef.current;
    if (!el || !end || n === 0) {
      setSpacer(0);
      return;
    }
    const last = end.offsetTop - sectionTop(n - 1);
    setSpacer(Math.max(0, el.clientHeight - last));
  }, [n]);
  // The section on screen: the last one whose start has been scrolled to.
  const locate = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    let cur = 0;
    for (let i = 0; i < n; i++) if (sectionTop(i) <= el.scrollTop + 1) cur = i;
    setCurrent(cur);
  }, [n]);
  useLayoutEffect(() => {
    measure();
    locate();
  }, [measure, locate, orderKey]);
  useEffect(() => {
    if (!scroller) return;
    const ro = new ResizeObserver(() => {
      measure();
      locate();
    });
    ro.observe(scroller);
    return () => ro.disconnect();
  }, [scroller, measure, locate]);
  const frame = useRef(0);
  const onScroll = () => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      locate();
    });
  };
  useEffect(() => () => cancelAnimationFrame(frame.current), []);
  const jump = (i: number) => rootRef.current?.scrollTo({ top: sectionTop(i), behavior: 'smooth' });
  // The group of the slide on screen: its bar, site and side are marked blue.
  const holdsSelected = (g: NavigatorGroup) => g.images.some((img) => img.series_instance_uid === selectedUID);

  return (
    <div className="flex h-full min-h-0 flex-col">
    <TypeBar sections={shown} current={current} onJump={jump} />
    <div
      ref={setRoot}
      className="subtle-scroll relative min-h-0 flex-1 overflow-y-auto"
      onScroll={onScroll}
      // A card folded or unfolded (Collapse): the sections' heights changed with no
      // reorder, scroll or resize, so the spacer and the section on screen are measured again.
      onTransitionEnd={(e) => {
        if (e.propertyName === 'height' && (e.target as HTMLElement).hasAttribute('data-preview')) {
          measure();
          locate();
        }
      }}
    >
      {shown.map((sec, i) => (
        <Fragment key={sec.type ?? 'other'}>
          <div ref={(el) => { marks.current[i] = el; }} aria-hidden />
          <button
            type="button"
            onClick={() => jump(i)}
            aria-current={current === i ? 'true' : undefined}
            style={{ position: 'sticky', top: 0, height: BAR_H }}
            className={cn(
              'z-10 flex w-full items-center gap-2 px-2 text-left text-xs font-semibold transition-colors',
              // Always the grey the section on screen used to take alone (decided 2026-09-24); the Type bar says which is on screen.
              'bg-accent text-foreground',
            )}
          >
            <span className="min-w-0 flex-1 truncate">{sec.label}</span>
            {/* The section's total (user, 2026-09-26: no seen count here). */}
            <span className="shrink-0 font-normal tabular-nums">{sec.count}</span>
          </button>
          {sec.groups.map((g) => (
            <div
              key={g.key}
              data-band={g.key}
              data-layout="list"
              className="relative mx-2 mb-5 mt-3 rounded-md pl-3"
              // The band a drop would land in is outlined while the pointer is over it.
              style={{ outline: drag?.target?.band === g.key ? '2px solid var(--color-primary)' : '2px solid transparent', outlineOffset: 2, transition: 'outline-color 120ms' }}
            >
              {/* The group's bar, from the top of its title to the bottom of its last card, flush
                  with both (user, 2026-09-26); blue while the slide on screen is one of its own. */}
              <span
                aria-hidden
                data-group-bar
                className={cn('absolute bottom-0 left-0 top-0 w-1 rounded-full transition-colors', holdsSelected(g) ? 'bg-blue-500' : 'bg-border')}
              />
              {/* A section whose one group says nothing needs no title for it. */}
              {(sec.groups.length > 1 || g.title !== 'Unspecified') && <GroupTitle group={g} active={holdsSelected(g)} />}
              <div className="flex flex-col gap-1.5">
                {g.images.map((img, k) => {
                  // Only the navigator's own images move; a series it does
                  // not know (the tail) has no position to change.
                  const movable = !!onReorder && !isTail(img);
                  return (
                    <ImageCard
                      key={img.image_id}
                      image={img}
                      thumbnail={thumbnail(img)}
                      selected={img.series_instance_uid === selectedUID}
                      pane={assignment?.(img.series_instance_uid)}
                      seen={tracked && !!img.seen_utc}
                      collapsed={collapsed(img)}
                      scrollMargin={{ top: BAR_H, bottom: 0 }}
                      onSelect={() => onSelect(img.series_instance_uid)}
                      movable={movable && g.images.length > 1}
                      onMoveUp={movable && k > 0 ? () => onReorder!(img.image_id, { beforeId: g.images[k - 1].image_id }) : undefined}
                      onMoveDown={movable && k < g.images.length - 1 ? () => onReorder!(img.image_id, { afterId: g.images[k + 1].image_id }) : undefined}
                      onPointerDown={movable ? onPointerDown(img.image_id) : undefined}
                      ghost={drag?.lead === img.image_id}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </Fragment>
      ))}
      <div ref={endRef} style={{ height: spacer }} aria-hidden />
      {drag && carried
        ? createPortal(
            <DragCard ref={overlayRef} image={carried} thumbnail={thumbnail(carried)} x={drag.x - drag.offsetX} y={drag.y - drag.offsetY} width={drag.width} />,
            document.body,
          )
        : null}
    </div>
    {tracked && studySeen.total > 0 && <SeenFooter tally={studySeen} onReset={onResetSeen!} collapseSeen={collapseSeen} onCollapseSeen={setCollapseSeen} />}
    </div>
  );
}

/**
 * The foot of the panel, where seen marks are kept (decided 2026-09-25): how
 * many of the study's images the reader has seen, a bar that fills as they go
 * (in ink, turning green once every image is seen; the count goes from muted
 * to ink then; the eye is green throughout: user, 2026-09-26), a reset and
 * the switch that collapses the seen ones, on one line above the bar: the
 * count left, the switch right, the reset midway (user, 2026-09-26/27). A
 * reset forgets every mark in the study, so it asks first, in place: the line
 * becomes the question (Cancel, then Reset) until it is answered.
 * No border: the panel keeps none (decided 2026-09-24).
 */
export function SeenFooter({
  tally,
  onReset,
  collapseSeen,
  onCollapseSeen,
}: {
  tally: SeenTally;
  onReset: () => void;
  collapseSeen: boolean;
  onCollapseSeen: (collapse: boolean) => void;
}) {
  const [asking, setAsking] = useState(false);
  // Nothing left to forget (the reset went through, or a refetch says so): no question.
  useEffect(() => {
    if (tally.seen === 0) setAsking(false);
  }, [tally.seen]);
  const done = tally.seen === tally.total;
  const pct = tally.total ? Math.round((tally.seen / tally.total) * 100) : 0;
  return (
    <div className="shrink-0 px-2 pb-2 pt-1.5 text-xs">
      <div className={cn('flex h-6 items-center', asking ? 'gap-0.5' : 'justify-between')}>
        {asking ? (
          <>
            {/* Cancel on the left, Reset on the right (user, 2026-09-26). */}
            <span className="min-w-0 flex-1 truncate font-medium">Reset seen marks?</span>
            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-muted-foreground" onClick={() => setAsking(false)}>
              Cancel
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-xs"
              onClick={() => {
                setAsking(false);
                onReset();
              }}
            >
              Reset
            </Button>
          </>
        ) : (
          <>
            <span
              data-seen-total
              className={cn('inline-flex min-w-0 items-center gap-1 tabular-nums', done ? 'text-foreground' : 'text-muted-foreground')}
            >
              <Eye size={14} aria-hidden className="shrink-0 text-emerald-600 dark:text-emerald-400" />
              <span className="truncate">
                Seen {tally.seen} / {tally.total}
              </span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 shrink-0 px-0.5 text-xs text-muted-foreground"
              disabled={tally.seen === 0}
              onClick={() => setAsking(true)}
            >
              Reset
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex shrink-0">
                  <Switch size="sm" checked={collapseSeen} onCheckedChange={onCollapseSeen} label="Collapse" ariaLabel="Collapse seen" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">Show seen images without their thumbnail</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
      <div
        role="progressbar"
        aria-label="Images seen"
        aria-valuemin={0}
        aria-valuemax={tally.total}
        aria-valuenow={tally.seen}
        className="mt-1 h-1 w-full overflow-hidden rounded-full bg-secondary"
      >
        <div className={cn('h-full rounded-full transition-[width,background-color] duration-200', done ? 'bg-emerald-500' : 'bg-foreground')} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Up to this many Types, the Type bar's buttons are wide and low, the icon beside the acronym. */
export const ROOMY_TYPES = 3;

/**
 * The study's Types at a glance, above the list and always on screen: an icon
 * and its acronym per section present, named in full by the app's tooltip,
 * the section on screen marked; a click scrolls to it (decided 2026-09-24:
 * the sections were hard to find). The buttons share the bar's width: a study
 * usually holds two or three Types, each then a wide, low button with its
 * icon beside its acronym; more stack the icon over the acronym, so all eight
 * (the seven and Other) still fit the narrow panel.
 */
function TypeBar({ sections, current, onJump }: { sections: NavigatorSection[]; current: number; onJump: (i: number) => void }) {
  if (!sections.length) return null;
  const roomy = sections.length <= ROOMY_TYPES;
  return (
    <div role="toolbar" aria-label="Image types" className="flex shrink-0 gap-0.5 p-1">
      {sections.map((sec, i) => {
        const Icon = sec.type ? TYPE_ICONS[sec.type] : OtherTypeIcon;
        const on = current === i;
        return (
          <Tooltip key={sec.type ?? 'other'}>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={sec.label}
                aria-current={on ? 'true' : undefined}
                onClick={() => onJump(i)}
                className={cn(
                  'inline-flex min-w-0 flex-1 basis-0 items-center justify-center rounded-md transition-colors',
                  roomy ? 'h-8 gap-1.5 px-2' : 'h-10 flex-col gap-0.5 px-0.5',
                  on ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                <Icon size={16} className="shrink-0" />
                <span className={cn('font-semibold leading-none tracking-wide', roomy ? 'text-[11px]' : 'text-[9px]')}>
                  {sec.type ? TYPE_ACRONYMS[sec.type] : 'OT'}
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{sec.label}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

/**
 * A group's header: the specimen (enterprise), then the site with its side as
 * anatomic pathology writes it — "A1 · Lung, Left" (the user, 2026-10-10:
 * one string, no side apart on the right) — in up to two lines, or
 * "Unspecified" when the group says nothing. `active` (the slide on screen is
 * in the group) turns it blue — the colour that reads as "selected", and
 * readable as text on the light panel too.
 */
function GroupTitle({ group, active = false }: { group: NavigatorGroup; active?: boolean }) {
  const tint = active ? 'text-blue-600 dark:text-blue-400' : null;
  const said = group.title !== 'Unspecified';
  return (
    <div className="mb-1.5 flex min-w-0">
      {/* Clipped past two lines, the whole of it is in the app's tooltip. */}
      <ClampedText
        text={group.title}
        className={cn(
          'line-clamp-2 min-w-0 flex-1 break-words text-sm leading-snug transition-colors',
          said ? 'font-semibold' : 'font-medium',
          tint ?? (said ? 'text-foreground' : 'text-muted-foreground'),
        )}
      />
    </div>
  );
}

/** What follows the pointer during a drag: the grabbed image, as a small row card. */
const DragCard = forwardRef<HTMLDivElement, { image: NavigatorImage; thumbnail: string | null; x: number; y: number; width: number }>(
  function DragCard({ image, thumbnail, x, y, width }, ref) {
    return (
      <div
        ref={ref}
        className="pointer-events-none fixed z-[1000] flex h-12 items-stretch overflow-hidden rounded-md bg-background shadow-xl"
        style={{ left: x, top: y, width: Math.min(width, DRAG_WIDTH), transform: 'rotate(-1.5deg)' }}
      >
        <div className="flex bg-muted/60">
          <OrdinalCell n={image.ordinal} />
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-2 px-2">
          <PreviewThumb url={thumbnail} fit="cover" enlarge={false} className="h-8 w-12 shrink-0" />
          <span className="min-w-0 truncate text-sm font-medium">{nameOf(image)}</span>
        </div>
      </div>
    );
  },
);
